import React, { useCallback, useMemo } from 'react';

import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubble';

import ParticipantStatusIcon from '@components/ParticipantStatusIcon';
import FollowMeIcon from './FollowMeIcon';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat';
import { useConference } from '../../../../DashboardContent/Dolby';
import { useRedirectToPrivateChat } from '@core/chat/StreamChat/hooks';
import { useMytaverse } from '@providers/MytaverseProvider';

import { sendCustomSentryBreadcrumb } from '@helpers/sentry';

import EventsService from '@services/EventsService';

import LocationOnIcon from '@public/images/pin.png';

import { ParticipantItemProps } from './interfaces';
import { IXRHrPersonaTypes } from '@clients/ek/interfaces';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { EventRole } from '@interfaces/event';

import { useStyles } from './styles';

const ParticipantItem = ({
  participant,
  currentParticipantId,
}: ParticipantItemProps) => {
  const classes = useStyles();
  const { setPreviewingParticipant } = useConference();
  const {
    currentEvent,
    currentParticipant,
    closeLeftMenu,
    teleportToParticipant,
    pendingFollowersData,
    acceptedFollowersData,
    isTeleporting,
  } = useMytaverseEvent();
  const { sendMessageToEvent, sendMessageToUnreal, user } = useMytaverse();
  const { setOpen: setOpenChat } = useChatState();

  const isCurrentParticipant =
    participant.participantId === currentParticipantId;
  const isCurrentParticipantAdmin =
    participant.eventRole === EventRole.Admin ||
    user?.hrData?.persona?.toUpperCase() === IXRHrPersonaTypes.HR_Facilitator;
  const isParticipantOnline = !!participant?.roomId;

  const handleParticipantPreview = useCallback(async () => {
    const participantProfile = await EventsService.getParticipantProfile(
      participant.participantId,
    );
    setPreviewingParticipant(participantProfile);
  }, [participant, setPreviewingParticipant]);

  const redirectToPrivateChat = useRedirectToPrivateChat();

  const handleChatIconClick = useCallback(async () => {
    sendCustomSentryBreadcrumb({
      message: `got to private chat with userId=${participant.participantId} from event drawer`,
    });
    closeLeftMenu();
    await redirectToPrivateChat(participant.participantId);
    setOpenChat(true);
  }, [closeLeftMenu, participant, redirectToPrivateChat, setOpenChat]);

  const handleTeleportToParticipant = useCallback(async () => {
    closeLeftMenu();
    await teleportToParticipant(participant);
  }, [teleportToParticipant, participant, closeLeftMenu]);

  const disableHoverListener = useMemo(
    () =>
      window.innerWidth < 1615
        ? participant.fullName.length < 14
        : participant.fullName.length < 28,
    [participant],
  );

  const sendFollowMeMessageToEvent = (action: WebsocketAction) => {
    if (!currentEvent?.eventId) return;
    if (action === WebsocketAction.InviteFollowingByAdmin) {
      sendMessageToUnreal({
        action,
        followerId: participant.participantId,
        ownerId: currentParticipantId,
        ownerName: currentParticipant?.firstName,
        ownerGameSessionId: currentParticipant?.gameSessionId,
        ownerRoomId: currentParticipant?.roomId,
      });
    }

    sendMessageToEvent(currentEvent.eventId, {
      action,
      followerId: participant.participantId,
      ownerId: currentParticipantId,
      ownerName: currentParticipant?.firstName,
      ownerGameSessionId: currentParticipant?.gameSessionId,
      ownerRoomId: currentParticipant?.roomId,
    });
  };

  return (
    <Box sx={classes.root} component="div">
      <Box sx={classes.rootPosition} component="div">
        <Avatar src={participant?.profileImage?.url} sx={classes.avatar}>
          {participant?.profileImage?.url}
        </Avatar>
        <Tooltip
          title={
            participant.name ||
            participant.fullName ||
            participant.email ||
            participant.participantId
          }
          disableHoverListener={disableHoverListener}
        >
          <Typography sx={classes.username} onClick={handleParticipantPreview}>
            {participant.name ||
              participant.fullName ||
              participant.email ||
              participant.participantId}
          </Typography>
        </Tooltip>
      </Box>

      <Box>
        <ParticipantStatusIcon
          isOnline={isParticipantOnline}
          className={isCurrentParticipant ? '' : 'onlineIndicator'}
          sx={classes.statusIcon}
        />
        {!isCurrentParticipant ? (
          <Box sx={classes.tools} className="helpers">
            {isCurrentParticipantAdmin && isParticipantOnline && (
              <FollowMeIcon
                participant={participant}
                pendingFollowersData={pendingFollowersData}
                acceptedFollowersData={acceptedFollowersData}
                currentParticipantId={currentParticipantId}
                sendFollowMeMessageToEvent={sendFollowMeMessageToEvent}
                sx={classes.iconButton}
              />
            )}
            <IconButton sx={classes.iconButton} onClick={handleChatIconClick}>
              <ChatBubbleOutlineOutlinedIcon sx={classes.icon} />
            </IconButton>
            <IconButton
              sx={classes.iconButton}
              disabled={isTeleporting}
              onClick={handleTeleportToParticipant}
            >
              <img src={LocationOnIcon} alt="" />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default ParticipantItem;
